/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Component, EventEmitter, Inject, OnInit, Output, HostListener, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WebpopupdialogComponent, WebDialogData } from 'src/app/web/webpopupdialog/webpopupdialog.component';
import { UserDataService } from '../../providers/user-data.service';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, UploadTaskSnapshot } from '@firebase/storage';
import { FirebaseAuthService } from '../../providers/firebase-auth.service';
import { UtilityFunctionsService } from '../../providers/utility-functions.service';
import { IGroupedCard, UPLOAD_STATUS } from 'src/app/models/groupedCard';
import { UploadFileService } from '../../providers/upload-file.service';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { CardsService } from '../../providers/cards.service';
import { MtlParserService } from '../../providers/mtl-parser.service';
import { POPUP_TEXT } from 'src/app/shared/text/popup-texts';

interface TextureSyncData {
  modelName?: string;
  modelId?: string;
  missingTextures?: TextureItem[];
  rootPath?: string;
}

interface TextureItem {
  name: string;
  pathToUpload?: string;
  uploaded: boolean;
  uploadProgress?: number;
  isUploading?: boolean;
}

enum BackendRequestState {
  IDLE = 'idle',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error'
}

@Component({
  selector: 'app-missing-sync-textures',
  templateUrl: './missing-textures-sync.component.html',
  styleUrls: ['./missing-textures-sync.component.scss']
})
export class MissingTexturesSyncComponent implements OnInit, AfterViewInit {

  @Output() webpopupdialogueMainBtnClick = new EventEmitter<string>();

  inputValue: string = '';
  textureSyncData: TextureSyncData;

  // textureSyncData: TextureSyncData = {
  //   rootPath: '/1748621140462_vgwsgnytlhd1',
  //   modelName: 'Mercedes_W13_Mercedes_W13_Mercedes_W13_Mercedes_W13',
  //   missingTextures: [
  //     { name: 'F1_Tire_Yellow_Metallic_long_texture_named_test_check.png', uploaded: true, uploadProgress: 100, isUploading: false },
  //     { name: 'F1_Tire_Yellow_Roughness.png', uploaded: true, uploadProgress: 100, isUploading: false },
  //     { name: 'F1_Tire_Yellow_Normal.png', uploaded: false, uploadProgress: 0, isUploading: false },
  //     { name: 'F1_Tire_Yellow_Height.png', uploaded: false, uploadProgress: 0, isUploading: false },
  //     { name: 'F1_Tire_Yellow_Metallic.png', uploaded: false, uploadProgress: 0, isUploading: false },
  //     { name: 'F1_Tire_Yellow_BaseColor.png', uploaded: false, uploadProgress: 0, isUploading: false },
  //     { name: 'F1_cockpit_BaseColor.png', uploaded: false, uploadProgress: 0, isUploading: false }
  //   ]
  // };

  selectedFiles: File[] = [];
  isProcessing: boolean = false;
  activeUploadTasks: any[] = []; // Store active upload tasks for cancellation

  // Backend request states
  backendRequestState: BackendRequestState = BackendRequestState.IDLE;
  retryCount: number = 0;
  maxRetries: number = 3;

  // Local drag state for dialog
  isDragOverDialog: boolean = false;

  heading: string = '';
  mainBtnTitle: string = '';
  inputPlaceHolder: string = '';
  isInputHidden: boolean;

  @ViewChild('texturesList') texturesList!: ElementRef<HTMLDivElement>;
  isTexturesScrollable = false;

  constructor(
    public userDataService: UserDataService,
    public firebaseAuth: FirebaseAuthService,
    private dialogRef: MatDialogRef<WebpopupdialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WebDialogData & {
      modelData: IGroupedCard;
    },
    private utilityFunctionsService: UtilityFunctionsService,
    private uploadFileService: UploadFileService,
    private cardsService: CardsService,
    private mtlParserService: MtlParserService
  ) {
    this.heading = data.heading || 'Model Details';
    this.mainBtnTitle = data.mainBtnTitle || 'Upload';
    this.inputPlaceHolder = data.inputPlaceHolder;
    this.inputValue = data.inputValue;
    this.isInputHidden = data.isInputHidden;

    // New logic: handle cases where we have missingTextures but no mappedTextures
    // This happens when MTL file is missing - just work with what we have
    const allTexturesNames = data.modelData.mappedTextures || [];
    const missingTexturesNames = data.modelData.missingTextures || [];

    let finalTextures: string[];

    if (allTexturesNames.length === 0 && missingTexturesNames.length > 0) {
      console.log('Working with missingTextures only (MTL file missing case)');
      // Use missingTextures as the complete list - all are missing
      finalTextures = missingTexturesNames;
    } else {
      console.log('Normal case - using mappedTextures');
      // Normal case - use mappedTextures
      finalTextures = allTexturesNames;
    }

    const missingTexturesSet = new Set(missingTexturesNames);

    // Create texture items: uploaded if NOT in missingTextures
    const allTextures = finalTextures.map(textureName => ({
      name: textureName,
      uploaded: !missingTexturesSet.has(textureName), // uploaded if NOT in missing list
      uploadProgress: missingTexturesSet.has(textureName) ? 0 : 100,
      isUploading: false,
      pathToUpload: undefined
    }));

    const rawModelName = data.modelData.title || data.modelData.id || '';
    const cleanedModelName = this.utilityFunctionsService.removeTimestampFromString(rawModelName);

    this.textureSyncData = {
      modelName: cleanedModelName,
      modelId: data.modelData.id, // Store modelId for Firestore updates
      rootPath: `${data.modelData.texturesRootPath}/`,
      missingTextures: allTextures // Now contains ALL textures with upload status
    };

    console.log('Texture sync data initialized:', this.textureSyncData);
    console.log('Total textures:', finalTextures.length);
    console.log('Missing textures:', missingTexturesSet.size);
  }

  ngOnInit(): void {
    // If all textures are already uploaded, automatically trigger backend request
    if (this.allTexturesUploaded && this.backendRequestState === BackendRequestState.IDLE) {
      console.log('All textures already uploaded, triggering initial backend request...');
      this.requestBackendRerender();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateScrollFlag(), 0);
  }

  onTexturesScroll(): void {
    this.updateScrollFlag();
  }

  private updateScrollFlag(): void {
    if (!this.texturesList) return;
    const el = this.texturesList.nativeElement;
    this.isTexturesScrollable = el.scrollHeight > el.clientHeight + 1;
  }

  // Dynamic getters for texture counts
  get totalTextures(): number {
    return this.textureSyncData.missingTextures?.length || 0;
  }

  get missingTexturesCount(): number {
    // Count uploaded textures AND textures that are currently uploading
    return this.textureSyncData.missingTextures?.filter(t => t.uploaded || t.isUploading).length || 0;
  }

  get allTexturesUploaded(): boolean {
    const textures = this.textureSyncData.missingTextures;
    return textures ? textures.every(t => t.uploaded) : false;
  }

  get headerText(): string {
    // Check if any file is an MTL file
    const hasMtlFile = this.textureSyncData.missingTextures?.some(t =>
      t.name.toLowerCase().endsWith('.mtl')
    ) || false;

    return hasMtlFile ? 'FILES' : 'TEXTURES';
  }

  get successMessage(): string {
    const hasMtlFile = this.textureSyncData.missingTextures?.some(t =>
      t.name.toLowerCase().endsWith('.mtl')
    ) || false;

    if (hasMtlFile) {
      return 'MTL file added, resuming render';
    } else {
      return 'Textures synced successfully';
    }
  }

  get isBackendProcessing(): boolean {
    return this.backendRequestState === BackendRequestState.PROCESSING;
  }

  get showRetryButton(): boolean {
    return this.allTexturesUploaded && this.backendRequestState === BackendRequestState.ERROR && this.retryCount < this.maxRetries;
  }

  get showSelectTexturesButton(): boolean {
    // Show only if there are textures that are neither uploaded nor uploading
    return this.textureSyncData.missingTextures?.some(t => !t.uploaded && !t.isUploading) || false;
  }

  get mainButtonText(): string {
    if (this.showRetryButton) return 'Retry';
    if (this.showSelectTexturesButton) return 'Select Folder';
    return '';
  }

  getTextureTooltip(texture: TextureItem): string {
    if (texture.uploaded) {
      return `${texture.name} - Uploaded successfully`;
    } else if (texture.isUploading) {
      return `${texture.name} - Uploading ${texture.uploadProgress}%`;
    } else {
      return `Click to select file for - ${texture.name}`;
    }
  }

  onBackClick(): void {
    if (this.isProcessing || this.isBackendProcessing) {
      const confirmed = confirm(POPUP_TEXT.SYSTEM_CONFIRM_MESSAGES.CLOSE_MISSING_TEXTURES_UPLOAD);
      if (confirmed) {
        this.abortAllUploads();
        this.resetDragState();
        this.dialogRef.close(false);
      }
    } else {
      this.resetDragState();
      this.dialogRef.close(false);
    }
  }

  onAbortClick(): void {
    console.log('Abort button clicked');
      const confirmed = confirm(POPUP_TEXT.SYSTEM_CONFIRM_MESSAGES.ABORT_MISSING_TEXTURES_UPLOAD);
      if (confirmed) {
        this.abortAllUploads();
        this.resetMapping();
        this.resetDragState();
        this.isProcessing = false;

        // Call abortCardRender from upload service to completely abort the model
        if (this.textureSyncData.modelId) {
          const card = this.uploadFileService.uploadModels.find(model => model.id === this.textureSyncData.modelId);
          if (card) {
            this.uploadFileService.abortCardRender(card);
            console.log(`Aborted rendering for model: ${this.textureSyncData.modelId}`);
          }
        }

        this.dialogRef.close(false);
      }

  }

  private abortAllUploads(): void {
    console.log('Aborting all active uploads...');
    this.activeUploadTasks.forEach(uploadTask => {
      if (uploadTask && typeof uploadTask.cancel === 'function') {
        uploadTask.cancel();
      }
    });
    this.activeUploadTasks = [];

    // Reset uploading states
    this.textureSyncData.missingTextures?.forEach(texture => {
      if (texture.isUploading) {
        texture.isUploading = false;
        texture.uploadProgress = 0;
      }
    });
  }

  private resetMapping(): void {
    console.log('Resetting texture mapping...');
    this.textureSyncData.missingTextures?.forEach(texture => {
      if (!texture.uploaded) {
        texture.isUploading = false;
        texture.uploadProgress = 0;
        texture.pathToUpload = undefined;
      }
    });
    this.selectedFiles = [];
  }

  private resetDragState(): void {
    this.isDragOverDialog = false;
  }

  async onMainBtnClick(): Promise<void> {
    if (this.showRetryButton) {
      // Retry backend request
      console.log('Retrying backend request...');
      await this.requestBackendRerender();
      return;
    }

    if (this.showSelectTexturesButton) {
      // Select folder for uploading
      console.log('onMainBtnClick - Select textures folder for model:', this.textureSyncData.modelName);
      await this.selectFolder();
      return;
    }
  }

  private async requestBackendRerender(): Promise<void> {
    if (this.isBackendProcessing) return;

    this.backendRequestState = BackendRequestState.PROCESSING;
    console.log(`Requesting backend rerender (attempt ${this.retryCount + 1}/${this.maxRetries})`);

    try {
      // Real backend request to trigger model render
      if (!this.textureSyncData.modelId) {
        throw new Error('Model ID is missing');
      }

      // Construct full model path from root path (remove trailing slash if present)
      const rootPath = this.textureSyncData.rootPath?.replace(/\/$/, '');
      const fullModelPath = rootPath || '';

      console.log(`Triggering model render for modelId: ${this.textureSyncData.modelId}, fullModelPath: ${fullModelPath}`);

      await this.cardsService.triggerModelRenderPostRequest(this.textureSyncData.modelId, fullModelPath);

      // Success
      console.log('Backend rerender request successful');
      this.backendRequestState = BackendRequestState.SUCCESS;
      this.retryCount = 0; // Reset retry count on success

      // Update model status in Firebase to readyToProcess
      if (this.textureSyncData.modelId) {
        try {
          await this.firebaseAuth.updateModelStatusToReadyToProcess(this.textureSyncData.modelId);
          console.log('Model status updated to readyToProcess in Firebase');

          // Update local groupedCard data in upload service
          this.updateLocalGroupedCardStatus();
        } catch (error) {
          console.error('Error updating model status in Firebase:', error);
          // Don't fail the whole process if Firebase update fails
        }
      }

      // Show success snackbar and close dialog automatically
      this.utilityFunctionsService.showSnackBar(this.successMessage, 0);
      this.dialogRef.close(true);

    } catch (error) {
      // Error
      console.error('Backend rerender request failed:', error);
      this.backendRequestState = BackendRequestState.ERROR;
      this.retryCount++;

      if (this.retryCount >= this.maxRetries) {
        // Show error snackbar after max retries
        this.utilityFunctionsService.showImportantSnackBar(
          'Failed to process model after multiple attempts. Please contact support.',
          0,
          5000
        );
        console.log('Max retries reached, showing error snackbar');
      }
    }
  }

  private updateLocalGroupedCardStatus(): void {
    // Find and update the card in uploadFileService.uploadModels array
    if (this.textureSyncData.modelId) {
      const cardIndex = this.uploadFileService.uploadModels.findIndex(card => card.id === this.textureSyncData.modelId);

      if (cardIndex !== -1) {
        const card = this.uploadFileService.uploadModels[cardIndex];

        // Update the card status so it doesn't show as error model anymore
        if (card.uploadStatus) {
          card.uploadStatus.status = UPLOAD_STATUS.PROCESSING; // Change from ERROR back to PROCESSING
          card.uploadStatus.progressMsg = 'Textures uploaded, processing...';
        }

        console.log(`Card ${this.textureSyncData.modelId} status updated in uploadFileService - ready for processing`);
      } else {
        console.warn(`Card ${this.textureSyncData.modelId} not found in uploadFileService.uploadModels`);
      }
    }
  }

  private async selectFolder(): Promise<void> {
    try {
      // Create file input element for folder selection
      const input = document.createElement('input');
      input.type = 'file';
      input.webkitdirectory = true; // Allow folder selection
      input.multiple = true;

      input.onchange = async (event: any) => {
        const files = Array.from(event.target.files) as File[];
        if (files.length > 0) {
          console.log('Selected files:', files);
          this.selectedFiles = files;
          await this.mapAndUploadTextures(files);
        }
      };

      input.click();
    } catch (error) {
      console.error('Error selecting folder:', error);
    }
  }

  private async mapAndUploadTextures(files: File[]): Promise<void> {
    this.isProcessing = true;

    // Create a map of lowercase filenames to actual files
    const fileMap = new Map<string, File>();
    files.forEach(file => {
      // Get just the filename without path (in case file.name contains path)
      const fileName = file.name.split('/').pop()?.toLowerCase() || file.name.toLowerCase();
      fileMap.set(fileName, file);
    });

    console.log(`Selected ${files.length} files from folder`);
    console.log('Available files:', Array.from(fileMap.keys()));

    // Map missing textures to selected files
    const texturesToUpload: TextureItem[] = [];
    const unmatchedTextures: string[] = [];

    this.textureSyncData.missingTextures?.forEach(texture => {
      if (!texture.uploaded && !texture.isUploading) {
        // Extract filename from the full path (e.g., "textures/F1_Tire_Yellow_BaseColor.png" -> "F1_Tire_Yellow_BaseColor.png")
        const textureFileName = texture.name.split('/').pop()?.toLowerCase() || texture.name.toLowerCase();

        if (fileMap.has(textureFileName)) {
          const matchedFile = fileMap.get(textureFileName)!;
          texture.pathToUpload = matchedFile.name;
          texturesToUpload.push(texture);
          console.log(`✓ Mapped texture: ${texture.name} -> ${matchedFile.name}`);
        } else {
          unmatchedTextures.push(texture.name);
        }
      }
    });

    console.log(`Mapping results: ${texturesToUpload.length} matched, ${unmatchedTextures.length} unmatched`);
    if (unmatchedTextures.length > 0) {
      console.log('Unmatched textures:', unmatchedTextures);
    }

    // Start upload for mapped textures
    if (texturesToUpload.length > 0) {
      console.log(`Starting upload for ${texturesToUpload.length} textures...`);
      await this.uploadMissingTextures(texturesToUpload);
    } else {
      console.log('No matching textures found in selected folder');
      this.utilityFunctionsService.showImportantSnackBar('No matching textures found in selected folder', 0, 4000);
      this.isProcessing = false;
    }
  }

  private async uploadMissingTextures(texturesToUpload: TextureItem[]): Promise<void> {
    const storage = getStorage();

    // Start all uploads simultaneously
    const uploadPromises = texturesToUpload.map(texture => this.uploadTextureToFirebase(texture, storage));

    // Wait for all uploads to complete
    await Promise.all(uploadPromises);

    this.isProcessing = false;
    console.log('All uploads completed to Firebase Storage');

    // Check if all textures are now uploaded and trigger backend request
    if (this.allTexturesUploaded) {
      console.log('All textures uploaded, triggering backend rerender request...');
      await this.requestBackendRerender();
    }
  }

  private async uploadTextureToFirebase(texture: TextureItem, storage: any): Promise<void> {
    return new Promise((resolve, reject) => {
      texture.isUploading = true;
      texture.uploadProgress = 0;

      // Find the actual file for this texture using the mapped file name
      const matchedFile = this.selectedFiles.find(file => {
        const userFileName = file.name.split('/').pop()?.toLowerCase() || file.name.toLowerCase();
        const textureFileName = texture.name.split('/').pop()?.toLowerCase() || texture.name.toLowerCase();
        return userFileName === textureFileName;
      });

      if (!matchedFile) {
        console.error(`File not found for texture: ${texture.name}`);
        texture.isUploading = false;
        reject(new Error(`File not found: ${texture.name}`));
        return;
      }

      // Use the original texture path (including directory structure) for Firebase storage
      // const filePath = `uploaded_models/${this.firebaseAuth.currentUser.uid}${this.textureSyncData.rootPath}${texture.name}`;
      const filePath = `${this.textureSyncData.rootPath}${texture.name}`;

      const storageRef = ref(storage, filePath);

      console.log(`🚀 Starting Firebase upload for ${texture.name} to ${filePath}`);

      const uploadTask = uploadBytesResumable(storageRef, matchedFile);

      // Store upload task for potential cancellation
      this.activeUploadTasks.push(uploadTask);

      uploadTask.on('state_changed',
        (snapshot: UploadTaskSnapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          texture.uploadProgress = Math.round(progress);
        },
        (error) => {
          console.error(`Upload failed for ${texture.name}:`, error);
          texture.isUploading = false;
          texture.uploadProgress = 0;

          // Remove from active tasks
          const taskIndex = this.activeUploadTasks.indexOf(uploadTask);
          if (taskIndex > -1) {
            this.activeUploadTasks.splice(taskIndex, 1);
          }

          reject(error);
        },
        async () => {
          texture.uploadProgress = 100;
          texture.isUploading = false;
          texture.uploaded = true;

          // Remove from active tasks
          const taskIndex = this.activeUploadTasks.indexOf(uploadTask);
          if (taskIndex > -1) {
            this.activeUploadTasks.splice(taskIndex, 1);
          }

          // Remove texture from missing textures in Firestore
          if (this.textureSyncData.modelId) {
            try {
              await this.firebaseAuth.removeTextureFromMissing(this.textureSyncData.modelId, texture.name);
              console.log(`✅ Upload and Firestore update completed for ${texture.name}`);
            } catch (error) {
              console.error(`Error updating Firestore for ${texture.name}:`, error);
              // Don't fail the upload if Firestore update fails
            }
          } else {
            console.log(`✅ Upload completed for ${texture.name}`);
          }

          resolve();
        }
      );
    });
  }

  // Handle files dropped from fullsize-dragdrop component
  onFilesDropped(files: NgxFileDropEntry[]): void {
    console.log('Files dropped from fullsize-dragdrop:', files);
    this.isDragOverDialog = false;
    this.processDroppedFiles(files);
  }

  private async processDroppedFiles(files: NgxFileDropEntry[]): Promise<void> {
    const fileList: File[] = [];

    for (const droppedFile of files) {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;

        await new Promise<void>((resolve) => {
          fileEntry.file(async (file: File) => {
            // If MTL file, parse for textures
            if (file.name.toLowerCase().endsWith('.mtl')) {
              const parsed = await this.mtlParserService.parseFile(file);
              console.log('Parsed textures from MTL:', parsed);
              this.addParsedTextures(parsed);
            }
            fileList.push(file);
            resolve();
          });
        });
      }
    }

    if (fileList.length > 0) {
      console.log('Processed dropped files:', fileList);
      this.selectedFiles = fileList;
      this.isDragOverDialog = false; // Reset drag state
      await this.mapAndUploadTextures(fileList);
    }
  }

  // Drag event handlers for the dialog
  @HostListener('dragenter', ['$event'])
  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.showSelectTexturesButton) {
      this.isDragOverDialog = true;
    }
  }

  @HostListener('dragleave', ['$event'])
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Only hide if we're leaving the dialog container completely
    if (!event.relatedTarget || !(event.currentTarget as Element)?.contains(event.relatedTarget as Node)) {
      this.isDragOverDialog = false;
    }
  }

  @HostListener('dragover', ['$event'])
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOverDialog = false;
  }

  // Individual texture file selection
  onTextureItemClick(texture: TextureItem): void {
    if (texture.uploaded || texture.isUploading) {
      return; // Don't allow clicking on already uploaded or uploading textures
    }

    console.log('Selecting file for texture:', texture.name);
    this.selectIndividualTextureFile(texture);
  }

  private async selectIndividualTextureFile(texture: TextureItem): Promise<void> {
    try {
      // Create file input element for single file selection
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '*/*'; // Accept all file types (textures and MTL files)
      input.multiple = false; // Single file only

      input.onchange = async (event: any) => {
        const file = event.target.files[0] as File;
        if (file) {
          console.log('Selected file for texture:', texture.name, 'File:', file.name);
          // If MTL, parse to extract textures before upload
          if (file.name.toLowerCase().endsWith('.mtl')) {
            const parsed = await this.mtlParserService.parseFile(file);
            console.log('Parsed textures from MTL:', parsed);
            this.addParsedTextures(parsed);
          }
          await this.uploadIndividualTexture(texture, file);
        }
      };

      input.click();
    } catch (error) {
      console.error('Error selecting individual texture file:', error);
    }
  }

  private async uploadIndividualTexture(texture: TextureItem, file: File): Promise<void> {
    texture.isUploading = true;
    texture.uploadProgress = 0;

    const storage = getStorage();
    const filePath = `${this.textureSyncData.rootPath}${texture.name}`;
    const storageRef = ref(storage, filePath);

    console.log(`🚀 Starting Firebase upload for individual texture ${texture.name} to ${filePath}`);

    const uploadTask = uploadBytesResumable(storageRef, file);

    // Store upload task for potential cancellation
    this.activeUploadTasks.push(uploadTask);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot: UploadTaskSnapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          texture.uploadProgress = Math.round(progress);
        },
        (error) => {
          console.error(`Upload failed for ${texture.name}:`, error);
          texture.isUploading = false;
          texture.uploadProgress = 0;

          // Remove from active tasks
          const taskIndex = this.activeUploadTasks.indexOf(uploadTask);
          if (taskIndex > -1) {
            this.activeUploadTasks.splice(taskIndex, 1);
          }

          this.utilityFunctionsService.showImportantSnackBar(`Failed to upload ${texture.name}`, 0, 4000);
          reject(error);
        },
        async () => {
          texture.uploadProgress = 100;
          texture.isUploading = false;
          texture.uploaded = true;

          // Remove from active tasks
          const taskIndex = this.activeUploadTasks.indexOf(uploadTask);
          if (taskIndex > -1) {
            this.activeUploadTasks.splice(taskIndex, 1);
          }

          // Remove texture from missing textures in Firestore
          if (this.textureSyncData.modelId) {
            try {
              await this.firebaseAuth.removeTextureFromMissing(this.textureSyncData.modelId, texture.name);
              console.log(`✅ Individual upload and Firestore update completed for ${texture.name}`);
            } catch (error) {
              console.error(`Error updating Firestore for ${texture.name}:`, error);
              // Don't fail the upload if Firestore update fails
            }
          } else {
            console.log(`✅ Individual upload completed for ${texture.name}`);
          }

          this.utilityFunctionsService.showSnackBar(`${texture.name} uploaded successfully`, 0);

          // Check if all textures are now uploaded and trigger backend request
          if (this.allTexturesUploaded) {
            console.log('All textures uploaded after individual upload, triggering backend rerender request...');
            await this.requestBackendRerender();
          }

          resolve();
        }
      );
    });
  }

  private addParsedTextures(textureNames: string[]): void {
    if (!textureNames || textureNames.length === 0) return;

    const newlyAdded: string[] = [];
    textureNames.forEach(name => {
      const exists = this.textureSyncData.missingTextures?.some(t => t.name.toLowerCase() === name.toLowerCase());
      if (!exists) {
        this.textureSyncData.missingTextures?.push({
          name,
          uploaded: false,
          uploadProgress: 0,
          isUploading: false,
          pathToUpload: undefined
        });
        newlyAdded.push(name);
      }
    });

    // update Firestore
    if (newlyAdded.length > 0 && this.textureSyncData.modelId) {
      this.firebaseAuth.appendTexturesToModel(this.textureSyncData.modelId, newlyAdded)
        .catch(err => console.error('Failed to append textures to model in Firestore', err));
    }
  }
}
